import { IFileToUpload } from "../interfaces/IFileToUpload";
import authModule from "../../../utilities/AuthModule";
import { getFilePreCommitInfo, getFolderPreCommitInfo } from "./SharePointRestServices";
import { ISiteFileDestinationInfo } from "../interfaces/ISiteFileDestinationInfo";
import { logger } from "../../../utilities/logger";
import { IFilePreCommitResult } from "../interfaces/IFilePreCommitResult";
import { FolderPreCommitRequest } from "../components/_v2/redux/api/local/api-local-interfaces";
import { DragAndDropHelper } from "../../../utilities/DragAndDropHelper";
import { SaveMode } from "../components/enums/SaveModeEnum";
import { willSaveAsPdf } from "../components/helpers/stringHelper";
import { encodeSharingUrl } from "./sharingUrlToken";
import { LocalizationModule } from "../../../utilities/LocalizationModule";
import { storedCopySave, StoredCopyStep, StoredCopyChoice } from "./storedCopySave";

// Office for the web refuses Document.getFileAsync for a document whose sensitivity label encrypts it
// (error 11001 "Network Problem", by design - office-js #5297/#6743). The stored copy in SharePoint or
// OneDrive is then downloaded instead: it stays encrypted, so a save keeps the label's protection.
// Above 100 MB the task pane would need over 400 MB to send it; Word and Excel for the web edit up to
// about 100 MB anyway.
export const STORED_COPY_MAX_BYTES = 100 * 1024 * 1024;
// Office for the web stores edits every few seconds to a few minutes, and neither Word's save() nor
// its saved flag waits for that (measured 2 s and 3 min, 2026-10-31/11-01). An older copy may miss
// the last edits, so the user chooses to save it or to wait for a newer one.
const STORED_COPY_FRESH_MS = 10_000;
const NEWER_COPY_CHECK_MS = 3_000;
// A look-up of the stored copy answers in well under a second; one that hangs counts as failed.
const STORED_COPY_LOOKUP_TIMEOUT_MS = 30_000;
// A fast download brings far more chunks than a progress bar can show, and each report renders the panel.
const DOWNLOAD_REPORT_EVERY_MS = 100;
// How Office for the web refuses a document whose label encrypts it ("Network Problem").
const OFFICE_REFUSED_ENCRYPTED = 11001;
const GRAPH_URL = "https://graph.microsoft.com/v1.0";
const STORED_DOCUMENT_URL = /^https:\/\/[^/]+\.sharepoint\.com\//i;
const COMPOUND_FILE_SIGNATURE = [0xd0, 0xcf, 0x11, 0xe0];
const ZIP_SIGNATURE = [0x50, 0x4b, 0x03, 0x04];

const hasSignature = (bytes: Uint8Array, signature: number[]): boolean => signature.every((value, index) => bytes[index] === value);

export enum OfficeFileReadFailure {
  // Office refused the document and there was no stored copy to fall back to.
  Unreadable = "unreadable",
  // Office for the web refused the document and its stored copy could not be downloaded.
  StoredCopyUnavailable = "storedCopyUnavailable",
  StoredCopyTooLarge = "storedCopyTooLarge",
  // The user cancelled the stored copy's save.
  Cancelled = "cancelled",
  // Office for the web refused an encrypted document, and the caller can't use its stored copy.
  Encrypted = "encrypted"
}

export interface IOfficeFileContent {
  bytes: Uint8Array | null;
  // An Office Open XML document is a zip; it is an OLE compound file only when encrypted.
  isCompoundFile: boolean;
  failure?: OfficeFileReadFailure;
}

const readFailed = (failure: OfficeFileReadFailure): IOfficeFileContent => ({ bytes: null, isCompoundFile: false, failure });

// The fields of a Graph driveItem the stored-copy download uses.
interface IStoredCopyItem {
  size: number;
  cTag: string;
  lastModifiedDateTime: string;
  "@microsoft.graph.downloadUrl": string;
}

const joinBytes = (chunks: Uint8Array[], length: number): Uint8Array => {
  const bytes = new Uint8Array(length);
  let offset = 0;
  for (const chunk of chunks) {
    bytes.set(chunk, offset);
    offset += chunk.length;
  }
  return bytes;
};

type LanguageStrings = ReturnType<typeof LocalizationModule.getLocaleStrings>;

export const getOfficeFileReadFailureMessage = (failure: OfficeFileReadFailure, strings: LanguageStrings): string => {
  switch (failure) {
    case OfficeFileReadFailure.StoredCopyTooLarge:
      return strings.OfficeFileStoredCopyTooLarge.replace("{placeholder}", `${STORED_COPY_MAX_BYTES / (1024 * 1024)} MB`);
    case OfficeFileReadFailure.Cancelled:
      return strings.OfficeFileStoredCopyCancelled;
    case OfficeFileReadFailure.StoredCopyUnavailable:
      return strings.OfficeFileStoredCopyUnavailable;
    default:
      return strings.OfficeFileUnreadable;
  }
};

const OPEN_XML_EXTENSIONS = ["docx", "docm", "dotx", "dotm", "xlsx", "xlsm", "xlsb", "xltx", "xltm", "pptx", "pptm", "potx", "potm", "ppsx", "ppsm"];

// Legacy .doc/.xls/.ppt files are compound files anyway; an Open XML one only is when its label encrypts it.
export const isEncryptedOfficePackage = (content: IOfficeFileContent, extension: string): boolean =>
  content.isCompoundFile && OPEN_XML_EXTENSIONS.includes(extension?.toLowerCase());

// The open document's content for an upload, or the message that says why it can't be uploaded.
// cancelled: the user cancelled, which the caller shows as such rather than as an error.
export const readOfficeFileForUpload = async (extension: string, saveMode: SaveMode, strings: LanguageStrings): Promise<{ bytes?: Uint8Array; failureMessage?: string; cancelled?: boolean }> => {
  // The API converts to PDF from the document's bytes, which it can't read when they're encrypted: an
  // encrypted document's stored copy isn't even fetched then.
  const isPdf = saveMode === SaveMode.SaveAsPdf;
  const fileContent = await FileService.GetFileContent(!isPdf);
  if (isPdf && (fileContent.failure === OfficeFileReadFailure.Encrypted || isEncryptedOfficePackage(fileContent, extension))) {
    return { failureMessage: strings.OfficeFileEncryptedAsPdf };
  }
  if (!fileContent.bytes) {
    const failureMessage = getOfficeFileReadFailureMessage(fileContent.failure, strings);
    return fileContent.failure === OfficeFileReadFailure.Cancelled ? { failureMessage, cancelled: true } : { failureMessage };
  }
  return { bytes: fileContent.bytes };
};

export class FileService {
  constructor() { }

  static getFilePreCommitCheck = async (files: IFileToUpload[], siteFileDestinationInfo: ISiteFileDestinationInfo, convertEmailToPDF: boolean, saveMode?: SaveMode): Promise<IFileToUpload[]> => {
    const accessToken = await authModule.getOneDriveAccessToken()
    return await Promise.all(files.map(async item => {
      let result: IFilePreCommitResult;

      if (item.isSelected) {
        if (item.isFolder && !item.parentFolderId) {
          const folderRequest: FolderPreCommitRequest = {
            accessToken,
            destinationUrl: siteFileDestinationInfo.destinationUrl,
            siteUrl: siteFileDestinationInfo.siteUrl,
            folderName: item.name
          }

          result = await getFolderPreCommitInfo(folderRequest);
        }
        //root file
        else if (!item.isFolder && !item.parentFolderId) {
          if ((DragAndDropHelper.isDroppedModeActive && item.isFileSystemEntry) || (!DragAndDropHelper.isDroppedModeActive && !item.isFileSystemEntry)) {
            result = await getFilePreCommitInfo({
              accessToken,
              autoFolderCreation: siteFileDestinationInfo.autoFolderCreation,
              fileName: willSaveAsPdf(item, convertEmailToPDF, saveMode) ? `${item.name}.pdf` : `${item.name}.${item.extension}`,
              destinationUrl: siteFileDestinationInfo.destinationUrl,
              siteUrl: siteFileDestinationInfo.siteUrl
            })
          } else {
            result = { exists: false, checkedOut: false, checkedOutBy: null, locked: false, lockedBy: null }
          }
        } else {
          result = { exists: false, checkedOut: false, checkedOutBy: null, locked: false, lockedBy: null }
        }
      } else {
        result = { exists: false, checkedOut: false, checkedOutBy: null, locked: false, lockedBy: null };
      }

      return {
        ...item,
        isDuplicate: result.exists,
        filePreCommitResult: result
      };
    }));
  };

  // useStoredCopy: false when the caller can't use an encrypted document anyway (Save as PDF).
  static GetFileContent = async (useStoredCopy = true): Promise<IOfficeFileContent> => {
    let officeErrorCode: number;
    try {
      const documentBytes = await new Promise<Uint8Array>((resolve, reject) => {
        // Restrict iPad to 64k
        const chunkSize = (Office.context.touchEnabled === true && Office.context.commerceAllowed === false) ? 65536 : 4194304;

        Office.context.document.getFileAsync(Office.FileType.Compressed, { sliceSize: chunkSize }, async (asyncResult) => {
          if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
            try {
              resolve(await FileService.getAllSlices(asyncResult.value));
            } catch (error) {
              reject(error);
            }
          } else {
            reject(asyncResult.error);
          }
        });
      });

      if (documentBytes?.length > 0) {
        return FileService.toFileContent(documentBytes);
      }
    } catch (error) {
      officeErrorCode = error?.code;
      logger(error, "FileService.ts (GetFileContent)", error?.code ? `Office error ${error.code} (${error.name})` : undefined);
    }

    const documentUrl: string = Office.context.document?.url;
    if (officeErrorCode === OFFICE_REFUSED_ENCRYPTED && Office.context.platform === Office.PlatformType.OfficeOnline && STORED_DOCUMENT_URL.test(documentUrl ?? "")) {
      return useStoredCopy ? await FileService.getStoredCopy(documentUrl) : readFailed(OfficeFileReadFailure.Encrypted);
    }

    return readFailed(OfficeFileReadFailure.Unreadable);
  };

  // The save panel (storedCopySave) shows this save from here on, through the upload: the caller ends it.
  private static getStoredCopy = async (documentUrl: string): Promise<IOfficeFileContent> => {
    const signal = storedCopySave.begin();
    try {
      // A token per request: the question can keep the save waiting longer than a token lasts. A failed
      // or hung look-up (a network blip) counts as no answer, so a later one or the copy already found is used.
      const getItem = async (): Promise<IStoredCopyItem | null> => {
        const request = new AbortController();
        const stop = () => request.abort();
        signal.addEventListener("abort", stop);
        const timer = setTimeout(stop, STORED_COPY_LOOKUP_TIMEOUT_MS);
        try {
          const headers = { Authorization: `Bearer ${await authModule.getGraphAccessToken()}` };
          const response = await fetch(`${GRAPH_URL}/shares/${encodeSharingUrl(documentUrl)}/driveItem`, { headers, signal: request.signal, cache: "no-store" });
          if (!response.ok) {
            logger(new Error(`The stored copy's look-up answered ${response.status}.`), "FileService.ts (getStoredCopy)");
            return null;
          }
          return await response.json();
        } catch (error) {
          if (signal.aborted) {
            throw error;
          }
          logger(error, "FileService.ts (getStoredCopy)");
          return null;
        } finally {
          clearTimeout(timer);
          signal.removeEventListener("abort", stop);
        }
      };

      let item = await getItem();
      if (!item) {
        return readFailed(OfficeFileReadFailure.StoredCopyUnavailable);
      }

      if (item.size <= STORED_COPY_MAX_BYTES) {
        const storedAt = new Date(item.lastModifiedDateTime);
        // A copy "from the future" means this computer's clock runs behind: how old it is is unknown.
        const age = Date.now() - storedAt.getTime();
        if (age < 0 || age > STORED_COPY_FRESH_MS) {
          let choice = await storedCopySave.ask(StoredCopyStep.Choice, storedAt);
          let newer: IStoredCopyItem | null = null;
          if (choice === StoredCopyChoice.WaitForChanges) {
            ({ choice, newer } = await FileService.waitForNewerCopy(item, getItem));
          }
          if (choice === StoredCopyChoice.Cancel) {
            return readFailed(OfficeFileReadFailure.Cancelled);
          }
          // The question gives way at once. A newer copy was just found; otherwise the answer may have
          // taken a while: download links expire, and Office may have stored a newer copy.
          storedCopySave.progress(StoredCopyStep.Downloading, 0, (newer ?? item).size);
          item = newer ?? (await getItem()) ?? item;
        }
      }
      if (item.size > STORED_COPY_MAX_BYTES) {
        return readFailed(OfficeFileReadFailure.StoredCopyTooLarge);
      }

      const storedBytes = await FileService.download(item, signal);
      return storedBytes ? FileService.toFileContent(storedBytes) : readFailed(OfficeFileReadFailure.StoredCopyTooLarge);
    } catch (error) {
      if (signal.aborted) {
        return readFailed(OfficeFileReadFailure.Cancelled);
      }
      logger(error, "FileService.ts (getStoredCopy)");
      return readFailed(OfficeFileReadFailure.StoredCopyUnavailable);
    }
  };

  // Checks for a newer stored copy until Office stores one (returned as newer) or the user stops waiting.
  // The user's answer counts at once, also while a check is still under way.
  private static waitForNewerCopy = async (item: IStoredCopyItem, getItem: () => Promise<IStoredCopyItem | null>): Promise<{ choice: StoredCopyChoice; newer?: IStoredCopyItem }> => {
    const answer: { choice?: StoredCopyChoice } = {};
    const answered = storedCopySave.ask(StoredCopyStep.WaitingForChanges, new Date(item.lastModifiedDateTime)).then(choice => { answer.choice = choice; });

    while (!answer.choice) {
      let timer: ReturnType<typeof setTimeout>;
      await Promise.race([answered, new Promise<void>(resolve => { timer = setTimeout(resolve, NEWER_COPY_CHECK_MS); })]);
      clearTimeout(timer);
      if (answer.choice) {
        break;
      }

      const check = getItem();
      // A check overtaken by the answer may still fail when the save is cancelled.
      check.catch(() => undefined);
      const next = await Promise.race([check, answered.then(() => null)]);
      if (next?.cTag && next.cTag !== item.cTag) {
        return { choice: StoredCopyChoice.SaveStoredCopy, newer: next };
      }
    }

    return { choice: answer.choice };
  };

  // Graph's pre-authenticated download link: Graph says a JavaScript app can't rely on /content's
  // redirect. Streamed for the progress bar; null once it passes STORED_COPY_MAX_BYTES.
  private static download = async (item: IStoredCopyItem, signal: AbortSignal): Promise<Uint8Array | null> => {
    storedCopySave.progress(StoredCopyStep.Downloading, 0, item.size);
    const downloadUrl = item["@microsoft.graph.downloadUrl"];
    if (!downloadUrl) {
      throw new Error("Graph gave no download link for the stored copy.");
    }
    const response = await fetch(downloadUrl, { signal });
    if (!response.ok) {
      throw new Error(`The stored copy's download answered ${response.status}.`);
    }

    // The encrypted download is about 1% larger than the size SharePoint lists.
    const total = Number(response.headers.get("Content-Length")) || item.size;
    if (total > STORED_COPY_MAX_BYTES) {
      response.body?.cancel().catch(() => undefined);
      return null;
    }
    storedCopySave.progress(StoredCopyStep.Downloading, 0, total);

    const reader = response.body.getReader();
    const chunks: Uint8Array[] = [];
    let received = 0;
    let reportedAt = 0;
    for (;;) {
      const { done, value } = await reader.read();
      if (done) {
        break;
      }
      received += value.length;
      if (received > STORED_COPY_MAX_BYTES) {
        reader.cancel().catch(() => undefined);
        return null;
      }
      chunks.push(value);
      const now = Date.now();
      if (now - reportedAt >= DOWNLOAD_REPORT_EVERY_MS) {
        reportedAt = now;
        storedCopySave.progress(StoredCopyStep.Downloading, received, Math.max(total, received));
      }
    }
    // Never a document: uploading it would save (or replace a file with) an empty one.
    if (!received) {
      throw new Error("The stored copy's download was empty.");
    }
    storedCopySave.progress(StoredCopyStep.Downloading, received, Math.max(total, received));

    const bytes = joinBytes(chunks, received);
    // Nor is anything but an Office file, such as an error page answered with 200.
    if (!hasSignature(bytes, COMPOUND_FILE_SIGNATURE) && !hasSignature(bytes, ZIP_SIGNATURE)) {
      throw new Error("The stored copy's download isn't an Office file.");
    }
    return bytes;
  };

  private static toFileContent = (bytes: Uint8Array): IOfficeFileContent => ({
    bytes,
    isCompoundFile: hasSignature(bytes, COMPOUND_FILE_SIGNATURE)
  });

  // Each slice becomes bytes straight away: as one growing number array (concatenated per slice) the
  // document cost many times its size.
  private static getAllSlices(file: Office.File): Promise<Uint8Array> {
    return new Promise(async (resolve, reject) => {
      const slices: Uint8Array[] = [];
      let size = 0;
      for (let sliceIndex = 0; sliceIndex < file.sliceCount; sliceIndex++) {
        const sliceReadPromise = new Promise((sliceResolve, sliceReject) => {
          file.getSliceAsync(sliceIndex, (asyncResult) => {
            if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
              const slice = Uint8Array.from(asyncResult.value.data);
              slices.push(slice);
              size += slice.length;
              sliceResolve(null);
            } else {
              file.closeAsync();
              sliceReject(`Error in reading slice no: ${sliceIndex} of the document`);
            }
          });
        });

        try {
          await sliceReadPromise;
        } catch (error) {
          return reject(error);
        }
      }

      if (!size) {
        return reject("Error while reading document. Please try it again.");
      }

      file.closeAsync();

      resolve(joinBytes(slices, size));
    });
  };
}
